import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { reloadHoliday } from "../lib/holiday";

const LABEL = { thanksgiving: "Thanksgiving", christmas: "Christmas" };
const FIELDS = [
  ["banner_text", "Top banner text", 200],
  ["popup_title", "Pop-up headline", 80],
  ["popup_text", "Pop-up message", 300],
  ["cta_label", "Button text", 40],
  ["page_title", "Deals page title", 80],
  ["page_intro", "Deals page intro", 300],
];
const input = "w-full h-10 rounded-xl border border-black/10 bg-white px-3 text-[13px] focus:outline-none focus:border-brand-teal";

const DealsEditor = ({ deals, items, onChange }) => {
  const [pick, setPick] = useState("");
  const byId = Object.fromEntries(items.map((i) => [i.id, i]));
  const add = () => {
    if (!pick || deals.some((d) => d.menu_item_id === pick)) return;
    onChange([...deals, { menu_item_id: pick, deal_price: byId[pick]?.price || 0, note: "" }]);
    setPick("");
  };
  const set = (i, patch) => onChange(deals.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return (
    <div className="space-y-2" data-testid="holiday-deals-editor">
      {deals.map((d, i) => (
        <div key={d.menu_item_id} data-testid={`holiday-deal-row-${d.menu_item_id}`} className="flex flex-wrap items-center gap-2 rounded-xl bg-brand-cream/40 p-2.5">
          <p className="flex-1 min-w-[160px] text-[13px] font-bold text-brand-charcoal">{byId[d.menu_item_id]?.name || d.menu_item_id}
            <span className="ml-2 font-normal text-brand-charcoal/50">reg. ${Number(byId[d.menu_item_id]?.price || 0).toFixed(2)}</span></p>
          <label className="text-[11px] text-brand-charcoal/60">Sale $
            <input type="number" min="0.01" step="0.01" value={d.deal_price} onChange={(e) => set(i, { deal_price: e.target.value })} data-testid={`holiday-deal-price-input-${d.menu_item_id}`} className="ml-1 w-24 h-9 rounded-lg border border-black/10 px-2 text-[13px]" /></label>
          <input value={d.note} onChange={(e) => set(i, { note: e.target.value })} maxLength={120} placeholder="Tag, e.g. Serves 10" className="w-40 h-9 rounded-lg border border-black/10 px-2 text-[12.5px]" />
          <button type="button" onClick={() => onChange(deals.filter((_, j) => j !== i))} aria-label="Remove deal" data-testid={`holiday-deal-remove-${d.menu_item_id}`} className="p-2 text-red-600 hover:bg-red-50 rounded-full"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="holiday-deal-pick" className={`${input} flex-1`}>
          <option value="">Choose a menu item to put on sale…</option>
          {items.filter((i) => !deals.some((d) => d.menu_item_id === i.id)).map((i) => <option key={i.id} value={i.id}>{i.category} · {i.name}</option>)}
        </select>
        <button type="button" onClick={add} disabled={!pick} data-testid="holiday-deal-add" className="inline-flex items-center gap-1 rounded-xl bg-brand-charcoal text-white px-4 text-[11px] font-bold uppercase tracking-[0.1em] disabled:opacity-40"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
    </div>
  );
};

export default function HolidayTab() {
  const [s, setS] = useState(null);
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState("thanksgiving");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/admin/holiday"), api.get("/admin/menu")]).then(([h, m]) => {
      setS(h.data);
      setItems((m.data.items || []).filter((i) => !i.quote_only));
      if (h.data.active !== "none") setEdit(h.data.active);
    });
  }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />;
  const cfg = s.themes[edit];
  const setCfg = (patch) => setS((x) => ({ ...x, themes: { ...x.themes, [edit]: { ...x.themes[edit], ...patch } } }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, themes: Object.fromEntries(Object.entries(s.themes).map(([k, v]) => [k, { ...v, deals: v.deals.map((d) => ({ ...d, deal_price: Number(d.deal_price) })) }])) };
      const { data } = await api.put("/admin/holiday", body);
      setS(data);
      reloadHoliday();
      toast.success(data.active === "none" ? "Saved — holiday theme is off" : `Saved — ${LABEL[data.active]} theme is live`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="holiday-tab" className="space-y-5 max-w-3xl">
      <div className="rounded-2xl bg-white border border-black/10 p-5 flex flex-wrap items-end gap-4">
        <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/60">Theme on the site
          <select value={s.active} onChange={(e) => setS({ ...s, active: e.target.value })} data-testid="holiday-active-select" className={`${input} mt-1 block w-56 normal-case tracking-normal font-semibold`}>
            <option value="none">Off (no holiday theme)</option>
            <option value="thanksgiving">Thanksgiving</option>
            <option value="christmas">Christmas</option>
          </select>
        </label>
        <button type="button" onClick={save} disabled={saving} data-testid="holiday-save" className="h-10 inline-flex items-center gap-2 rounded-full bg-brand-teal text-white px-6 text-[12px] font-bold uppercase tracking-[0.12em] disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
        </button>
        <a href="/holiday" target="_blank" rel="noreferrer" data-testid="holiday-preview-link" className="h-10 inline-flex items-center gap-1.5 text-[12px] font-bold text-brand-teal">Preview deals page <ExternalLink className="h-3.5 w-3.5" /></a>
      </div>
      <div className="flex gap-2">
        {Object.keys(LABEL).map((t) => (
          <button key={t} type="button" onClick={() => setEdit(t)} data-testid={`holiday-edit-${t}`} className={`rounded-full px-4 py-2 text-[11.5px] font-bold uppercase tracking-[0.1em] border ${edit === t ? "bg-brand-charcoal text-white border-brand-charcoal" : "bg-white border-black/10"}`}>
            Edit {LABEL[t]}{s.active === t && " · Live"}
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        {FIELDS.map(([k, label, max]) => (
          <label key={k} className="block text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/60">{label}
            <input value={cfg[k] || ""} maxLength={max} onChange={(e) => setCfg({ [k]: e.target.value })} data-testid={`holiday-field-${k}`} className={`${input} mt-1 normal-case tracking-normal font-normal`} />
          </label>
        ))}
        <p className="pt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/60">Deal items (sale price applies to retail customers at checkout)</p>
        <DealsEditor deals={cfg.deals} items={items} onChange={(deals) => setCfg({ deals })} />
      </div>
    </div>
  );
}
